import { fmtInterval } from "./format";

/**
 * Read a cadence the way someone would type it: "50s", "2m", "1m30s", "1.5m",
 * or a bare "50".
 *
 * Replaces a number field paired with a unit dropdown, which made the
 * sub-minute end — the end anyone actually fiddles with — cost four
 * interactions. A bare number means seconds for the same reason; the field
 * echoes the parsed result back as it is typed, so the rule is visible rather
 * than assumed.
 *
 * Every rejection carries a message. The version this replaces returned early
 * on anything it disliked, leaving the field showing one value while the
 * store kept another, with nothing said.
 */
export type ParsedInterval = { ms: number } | { error: string };

const UNITS: { names: string[]; ms: number }[] = [
	{ names: ["h", "hr", "hrs", "hour", "hours"], ms: 60 * 60_000 },
	{ names: ["m", "min", "mins", "minute", "minutes"], ms: 60_000 },
	{ names: ["s", "sec", "secs", "second", "seconds"], ms: 1000 },
];

function unitMs(token: string): number | null {
	for (const u of UNITS) if (u.names.includes(token)) return u.ms;
	return null;
}

/** One number optionally followed by a unit, e.g. "1.5m" or "90". */
const PART = /(\d+(?:\.\d+)?)\s*([a-z]*)/y;

export function parseInterval(input: string, minMs: number): ParsedInterval {
	const text = input.trim().toLowerCase();
	if (text === "") return { error: "Enter a cadence" };

	PART.lastIndex = 0;
	let total = 0;
	let parts = 0;
	const usedUnits = new Set<number>();

	while (PART.lastIndex < text.length) {
		// Skip separators between parts ("1m 30s", "2h, 15m").
		const rest = text.slice(PART.lastIndex);
		const lead = rest.length - rest.replace(/^[\s,]+/, "").length;
		if (lead > 0) {
			PART.lastIndex += lead;
			continue;
		}

		const m = PART.exec(text);
		if (!m) return { error: "Try something like 30s, 90s or 5m" };

		const value = Number(m[1]);
		if (!Number.isFinite(value)) return { error: "Try something like 30s, 90s or 5m" };

		const token = m[2] ?? "";
		let factor: number;
		if (token === "") {
			// A bare number is only meaningful on its own — "1m 30" is ambiguous
			// enough that guessing at it would be worse than asking.
			if (parts > 0) return { error: "Give every part a unit, e.g. 1m 30s" };
			factor = 1000;
		} else {
			const found = unitMs(token);
			if (found === null) return { error: `Unknown unit "${token}"` };
			factor = found;
		}

		if (usedUnits.has(factor)) return { error: "That names the same unit twice" };
		usedUnits.add(factor);

		total += value * factor;
		parts += 1;
	}

	if (parts === 0) return { error: "Try something like 30s, 90s or 5m" };

	// Whole seconds: a cadence is a polling interval, and storing 46,662ms
	// would mean the echo under the field could never match what was saved.
	const ms = Math.round(total / 1000) * 1000;
	if (ms <= 0) return { error: "Must be more than zero" };
	if (ms < minMs) return { error: `Must be at least ${fmtInterval(minMs)}` };
	return { ms };
}

/**
 * Render an interval without losing a part of it.
 *
 * `fmtInterval` collapses to a single unit, which suits a table column and
 * misleads under an input: it shows 90s as "2m", so echoing a typed "1m30s"
 * through it would report a value half a minute off what gets saved.
 */
export function fmtIntervalExact(ms: number): string {
	const totalSeconds = Math.max(0, Math.round(ms / 1000));
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	const parts: string[] = [];
	if (hours > 0) parts.push(`${hours}h`);
	if (minutes > 0) parts.push(`${minutes}m`);
	if (seconds > 0) parts.push(`${seconds}s`);
	return parts.length > 0 ? parts.join(" ") : "0s";
}
